import { tr, useArkmeLocale } from './locale.js'
import { useArkmeLivePhotoPlayback } from './live-photo-playback.js'
import { ArkmeLivePhotoBadge } from './ArkmeLivePhotoBadge.js'
import { arkmeMarkdownPlainText } from '../markdown.js'
import { preserveTextTogglePosition } from './preserve-text-toggle-position.js'
import { ArkmeMarkdownBody } from './ArkmeMarkdownBody.js'
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent } from 'react'
import { createPortal } from 'react-dom'
import { ArkmeFileIcon } from './ArkmeFileIcon.js'
import { arkmeTheme } from './arkme-theme.js'
import { ARKME_DEFAULT_SHARE_WEBSITE } from '../types.js'
import type {
  ArkmeContentBlock, ArkmeLinkMetadata, ArkmeLongArticleDetail, ArkmeRelatedRecordingItem,
  ArkmeSharedRecordingPreview, ArkmeTimelineItem, ArkmeTimelineMentionTarget, ArkmeUploadedAsset,
} from '../types.js'
import { callArkme } from './api.js'
import { ArkmeCallRecordContent } from './ArkmeCallRecordContent.js'
import { ArkmeLongArticleBody } from './ArkmeLongArticleBody.js'
import { ArkmeLongArticleDialog, ArkmeLongArticleSnapshotDialog } from './ArkmeLongArticleDialog.js'
import { ArkmeVoiceContent, arkmeVoiceMediaUrl } from './ArkmeVoiceContent.js'
import {
  ArkmeFileActionNavButton, ArkmeFileActionToast, ArkmeFileViewer, ArkmeFileActions,
  arkmeLocalFileUrl, arkmeFileSize, arkmeCanPreviewFile, useArkmeFileActionNotice, useArkmeOriginal,
} from './ArkmeFileViewer.js'
import { arkmeCanInlineLocalFile, arkmeVisibleUploadFraction } from '../file-transfer-contract.js'
import { createArkmeSdk } from '../sdk/index.js'
import { ArkmeRichText, type ArkmeMentionClickHandler, type ArkmeMentionClickPredicate } from './ArkmeRichText.js'
import { useReadMentionMembers } from './mention-read-status.js'
import { arkmeEmojiPlainText } from './arkme-emoji.js'
import type { ArkmeLinkLabelMode, ArkmeLinkRenderer } from './ArkmeLinkText.js'
import { retainPartialTimelineMedia } from './timeline-media.js'

const mediaRoute = '/arkme-self/api/media'
const textCollapseCharacterThreshold = 300
const textCollapseNewlineThreshold = 5
const textCollapseMaxLines = 5
const mediaGap = 5
const fileSdk = createArkmeSdk()

const styles: Record<string, CSSProperties> = {
  stack: { width: 'max-content', maxWidth: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 8 },
  text: { width: 'max-content', maxWidth: '100%', margin: 0, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', wordBreak: 'break-word', fontSize: 14, lineHeight: 1.62 },
  collapsedText: { display: '-webkit-box', overflow: 'hidden', WebkitBoxOrient: 'vertical', WebkitLineClamp: textCollapseMaxLines },
  textFrame: { position: 'relative', width: '100%', maxWidth: '100%' },
  textFade: { position: 'absolute', right: 0, bottom: 22, left: 0, height: 28, pointerEvents: 'none', background: 'linear-gradient(180deg, rgba(255,255,255,0), var(--arkme-bubble-fade, rgba(238,243,255,.96)))' },
  collapseToggle: { display: 'block', marginLeft: 'auto', border: 0, padding: '2px 0', background: 'transparent', color: 'var(--dsw-alias-label-tertiary, #9097a1)', cursor: 'pointer', font: 'inherit', fontSize: 12, lineHeight: '18px' },
  mediaGrid: { display: 'grid', gap: mediaGap, maxWidth: '100%' },
  mediaTile: { position: 'relative', display: 'block', width: '100%', aspectRatio: '1', overflow: 'hidden', border: 0, padding: 0, borderRadius: 8, background: 'rgba(127,127,127,.10)', cursor: 'pointer' },
  mediaImage: { display: 'block', width: '100%', height: '100%', objectFit: 'cover' },
  sticker: { display: 'block', width: 148, maxWidth: '42vw', height: 148, maxHeight: '24vh', objectFit: 'contain', cursor: 'pointer' },
  videoPreview: { display: 'block', width: '100%', height: '100%', objectFit: 'cover', background: '#111', pointerEvents: 'none' },
  videoBadge: { position: 'absolute', left: 6, bottom: 6, display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 5px', borderRadius: 6, background: 'rgba(0,0,0,.62)', color: '#fff', fontSize: 10, lineHeight: '14px' },
  file: { width: 'max-content', maxWidth: '100%', minWidth: 0, minHeight: 48, display: 'flex', alignItems: 'center', gap: 10, padding: '4px 0', boxSizing: 'border-box', borderRadius: 8, color: 'inherit', background: 'transparent', textDecoration: 'none' },
  fileIconBox: { width: 40, height: 40, flex: 'none', display: 'grid', placeItems: 'center' },
  fileName: { minWidth: 0, display: 'block', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', fontSize: 12, lineHeight: '18px' },
  article: { width: 400, maxWidth: '100%', display: 'flex', flexDirection: 'column', padding: 0, boxSizing: 'border-box', borderRadius: 0, background: 'transparent', color: 'inherit' },
  articleButton: { border: 0, font: 'inherit', textAlign: 'left', cursor: 'pointer' },
  articleHeading: { display: 'flex', alignItems: 'center', gap: 6 },
  articleIcon: { width: 16, height: 16, flex: 'none', color: 'var(--dsw-alias-state-business-primary, #8295e8)' },
  articleTitle: { margin: 0, minWidth: 0, display: '-webkit-box', overflow: 'hidden', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2, overflowWrap: 'anywhere', fontSize: 15, lineHeight: '22px', fontWeight: 600 },
  articlePreview: { maxWidth: '100%', margin: '8px 0 0', display: '-webkit-box', overflow: 'hidden', WebkitBoxOrient: 'vertical', overflowWrap: 'anywhere', color: 'var(--dsw-alias-label-secondary, #68707c)', fontSize: 14, lineHeight: '20px' },
  articleMeta: { margin: '8px 0 0', display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--dsw-alias-label-tertiary, #9097a1)', fontSize: 12, lineHeight: '14px' },
  articleWordIcon: { width: 14, height: 14, flex: 'none' },
  forwardCard: { width: '100%', minWidth: 0, overflow: 'hidden', color: arkmeTheme.text },
  forwardTitle: { margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 14, lineHeight: '22px', fontWeight: 600 },
  forwardLines: { marginTop: 7, display: 'flex', flexDirection: 'column', gap: 0 },
  forwardLine: { margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: arkmeTheme.secondary, fontSize: 13, lineHeight: 1.65 },
  sharedRecordingCard: { width: '100%', minWidth: 0, overflow: 'hidden', color: arkmeTheme.text },
  sharedRecordingTop: { display: 'flex', alignItems: 'baseline', gap: 8, minWidth: 0 },
  sharedRecordingTitle: { flex: 1, minWidth: 0, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 14, lineHeight: 1.35, fontWeight: 600 },
  sharedRecordingTime: { flex: 'none', color: arkmeTheme.tertiary, fontSize: 12, lineHeight: '16px' },
  sharedRecordingSummary: { margin: '6px 0 0', display: '-webkit-box', overflow: 'hidden', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2, color: arkmeTheme.secondary, fontSize: 13, lineHeight: 1.45 },
  sharedRecordingParticipants: { margin: '7px 0 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: arkmeTheme.tertiary, fontSize: 12, lineHeight: 1.3 },
  inlineLink: { display: 'inline-flex', alignItems: 'baseline', gap: 4, maxWidth: '100%', color: 'var(--dsw-alias-state-business-primary, #007aff)', textDecoration: 'none', cursor: 'pointer', verticalAlign: 'baseline' },
  inlineLinkTitle: { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  inlineRawLinkTitle: { minWidth: 0, overflowWrap: 'anywhere', wordBreak: 'break-word', whiteSpace: 'normal' },
  previewOverlay: { position: 'fixed', inset: 0, zIndex: 1000, display: 'grid', placeItems: 'center', padding: '20px 10px', boxSizing: 'border-box', background: 'rgba(0,0,0,.5)' },
  previewBody: { position: 'relative', width: '100%', height: '100%', minWidth: 0, minHeight: 0 },
  previewStage: { position: 'absolute', top: 30, left: 0, right: 0, bottom: 52, overflow: 'hidden', borderRadius: 12 },
  previewViewport: { width: '100%', height: '100%', overflowX: 'hidden', overscrollBehavior: 'contain', touchAction: 'none' },
  previewCanvasContained: { width: '100%', height: '100%', overflow: 'hidden' },
  previewCanvasWidth: { width: '100%', minHeight: '100%', display: 'block' },
  previewImageContained: { display: 'block', width: '100%', height: '100%', objectFit: 'contain', userSelect: 'none' },
  previewImageWidth: { display: 'block', width: '100%', height: 'auto', userSelect: 'none' },
  previewMedia: { display: 'block', width: '100%', height: '100%', objectFit: 'contain', userSelect: 'none' },
  previewClose: { position: 'absolute', top: 0, right: 8, width: 28, height: 28, display: 'grid', placeItems: 'center', padding: 0, border: 0, borderRadius: '50%', background: 'transparent', color: 'rgba(255,255,255,.92)', cursor: 'pointer' },
  previewActions: { position: 'absolute', left: 0, right: 0, bottom: 10, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' },
  previewActionWideGap: { width: 24, flex: 'none' },
  previewActionPair: { gap: 12 },
  previewActionToast: { position: 'fixed', inset: 0, alignItems: 'center', zIndex: 1 },
}

function ensureUrlScheme(value: string): string {
  return /^https?:\/\//iu.test(value) ? value : `https://${value}`
}

function linkHostCandidates(shareWebsite: string): Set<string> {
  const shortLinkHost = 'ji' + 'wo.cc'
  const hosts = new Set([shortLinkHost, `www.${shortLinkHost}`, 'app.arkme.ai'])
  try {
    const parsed = new URL(ensureUrlScheme(shareWebsite.trim()))
    if (parsed.hostname.trim() !== '') hosts.add(parsed.hostname.toLowerCase())
    if (parsed.host.trim() !== '') hosts.add(parsed.host.toLowerCase())
  } catch {
    // Keep the built-in production hosts when the runtime config is unavailable.
  }
  return hosts
}

export function arkmeMessageCopyLinkSidFromUrl(rawUrl: string, shareWebsite = ARKME_DEFAULT_SHARE_WEBSITE): string | undefined {
  let parsed: URL
  try {
    parsed = new URL(ensureUrlScheme(rawUrl.trim()))
  } catch {
    return undefined
  }
  if (parsed.protocol !== 'https:' || parsed.search !== '' || parsed.hash !== '' || parsed.username !== '' || parsed.password !== '') return undefined
  if (!linkHostCandidates(shareWebsite).has(parsed.host.toLowerCase()) && !linkHostCandidates(shareWebsite).has(parsed.hostname.toLowerCase())) return undefined
  const match = /^\/s\/([0-9A-Za-z]{16})$/u.exec(parsed.pathname)
  return match?.[1]
}

function ArkmeLinkIcon({ size = 16 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden focusable="false">
    <path d="M6.6 9.4L9.4 6.6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M6.05 5.1l.8-.8a3 3 0 0 1 4.24 4.24l-.8.8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M9.95 10.9l-.8.8a3 3 0 0 1-4.24-4.24l.8-.8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
}

function ArkmeMessageCopyLink({
  href,
  text,
  sid,
  linkLabelMode,
  onMessageCopyLinkOpen,
}: {
  href: string
  text: string
  sid: string
  linkLabelMode: ArkmeLinkLabelMode
  onMessageCopyLinkOpen?: (sid: string) => void
}) {
  const open = () => {
    if (onMessageCopyLinkOpen !== undefined) {
      onMessageCopyLinkOpen(sid)
      return
    }
    if (typeof window !== 'undefined') window.open(href, '_blank', 'noopener,noreferrer')
  }
  return <span
    role="link"
    tabIndex={0}
    style={styles.inlineLink}
    data-arkme-inline-link="message-copy-link"
    title={href}
    onClick={event => {
      event.stopPropagation()
      open()
    }}
    onKeyDown={event => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.stopPropagation()
        event.preventDefault()
        open()
      }
    }}
  >
    <ArkmeLinkIcon />
    <span style={linkLabelMode === 'raw' ? styles.inlineRawLinkTitle : styles.inlineLinkTitle} data-arkme-link-label="true">{linkLabelMode === 'raw' ? text : '快记分享链接'}</span>
  </span>
}

function ArkmeMessageRichText({
  text,
  textFormat,
  textStyle,
  highlightMentions,
  linkLabelMode,
  mentionTargets,
  readMentionMembers,
  shareWebsite,
  onMessageCopyLinkOpen,
  onMentionClick,
  isMentionClickable,
}: {
  text: string
  textFormat?: 'plain' | 'markdown'
  textStyle?: Pick<CSSProperties, 'fontSize' | 'lineHeight'> | undefined
  highlightMentions: boolean
  linkLabelMode: ArkmeLinkLabelMode
  mentionTargets?: readonly ArkmeTimelineMentionTarget[]
  readMentionMembers?: ReadonlySet<string>
  shareWebsite?: string
  onMessageCopyLinkOpen?: (sid: string) => void
  onMentionClick?: ArkmeMentionClickHandler
  isMentionClickable?: ArkmeMentionClickPredicate
}) {
  const renderLink: ArkmeLinkRenderer = link => {
    const sid = arkmeMessageCopyLinkSidFromUrl(link.href, shareWebsite)
    if (sid === undefined) return undefined
    return <ArkmeMessageCopyLink
      href={link.href}
      text={link.text}
      sid={sid}
      linkLabelMode={linkLabelMode}
      {...(onMessageCopyLinkOpen === undefined ? {} : { onMessageCopyLinkOpen })}
    />
  }
  if (textFormat === 'markdown') return <ArkmeMarkdownBody
    text={text}
    textStyle={textStyle}
    highlightMentions={highlightMentions}
    renderLink={renderLink}
    {...(mentionTargets === undefined ? {} : { mentionTargets })}
    {...(readMentionMembers === undefined ? {} : { readMentionMembers })}
    {...(onMentionClick === undefined ? {} : { onMentionClick })}
    {...(isMentionClickable === undefined ? {} : { isMentionClickable })}
  />
  return <ArkmeRichText
    text={text}
    highlightMentions={highlightMentions}
    linkLabelMode={linkLabelMode}
    {...(mentionTargets === undefined ? {} : { mentionTargets })}
    {...(readMentionMembers === undefined ? {} : { readMentionMembers })}
    renderLink={renderLink}
    {...(onMentionClick === undefined ? {} : { onMentionClick })}
    {...(isMentionClickable === undefined ? {} : { isMentionClickable })}
  />
}

export function arkmeContentMediaUrl(block: ArkmeContentBlock): string {
  if (block.localFileRef !== undefined) return arkmeLocalFileUrl(block.localFileRef)
  return `${mediaRoute}?ref=${encodeURIComponent(block.mediaRef)}`
}

function mediaAttemptUrl(block: ArkmeContentBlock, attempt: number): string {
  const url = arkmeContentMediaUrl(block)
  return attempt > 0 ? `${url}${url.includes('?') ? '&' : '?'}retry=${String(attempt)}` : url
}

function UploadProgress({ block }: { block: ArkmeContentBlock }) {
  if (block.uploadProgress === undefined || block.uploadProgress.phase === 'ready') return null
  const percent = Math.floor(arkmeVisibleUploadFraction(block.uploadProgress) * 100)
  return <span role="progressbar" aria-label={`上传 ${block.fileName}`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent} style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', background: 'rgba(0,0,0,.38)', color: '#fff', borderRadius: 8, fontSize: 12, pointerEvents: 'none' }}><span style={{ width: 36, height: 36, borderRadius: '50%', border: '2px solid rgba(255,255,255,.8)', display: 'grid', placeItems: 'center' }}>{percent}%</span></span>
}

function durationLabel(durationSec?: number): string {
  const total = Math.max(0, Math.round(durationSec ?? 0))
  const minutes = Math.floor(total / 60)
  return `${String(minutes).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
}

function normalizedTextLength(value: string): number {
  return Array.from(value.replace(/\[(?:jm_emoji|im_emoji):[^\]\r\n]+\]/gu, '●').trim()).length
}

function isEmojiOnly(value: string): boolean {
  const compact = value.replace(/\s/gu, '')
  if (compact === '') return false
  const withoutCustomEmoji = compact.replace(/\[(?:jm_emoji|im_emoji):[^\]\r\n]+\]/gu, '')
  return withoutCustomEmoji.replace(/[\p{Extended_Pictographic}\p{Emoji_Presentation}\u200D\uFE0F\u20E3]/gu, '') === ''
}

function shouldCollapseText(value: string): boolean {
  if (isEmojiOnly(value)) return false
  const newlineCount = (value.match(/\n/gu) ?? []).length
  return normalizedTextLength(value) > textCollapseCharacterThreshold || newlineCount > textCollapseNewlineThreshold
}

function LongText({
  text,
  textFormat,
  highlightMentions = false,
  collapseText = true,
  expanded = false,
  linkLabelMode,
  mentionTargets,
  readMentionMembers,
  shareWebsite,
  onMessageCopyLinkOpen,
  onMentionClick,
  isMentionClickable,
}: {
  text: string
  textFormat?: 'plain' | 'markdown'
  highlightMentions?: boolean
  collapseText?: boolean
  expanded?: boolean
  linkLabelMode: ArkmeLinkLabelMode
  mentionTargets?: readonly ArkmeTimelineMentionTarget[]
  readMentionMembers?: ReadonlySet<string>
  shareWebsite?: string
  onMessageCopyLinkOpen?: (sid: string) => void
  onMentionClick?: ArkmeMentionClickHandler
  isMentionClickable?: ArkmeMentionClickPredicate
}) {
  useArkmeLocale()
  const markdown = textFormat === 'markdown'
  const markdownBody = useRef<HTMLDivElement>(null)
  const [markdownOverflow, setMarkdownOverflow] = useState(false)
  const markdownHeight = 160
  useLayoutEffect(() => {
    const element = markdownBody.current
    if (element === null || !collapseText || expanded) return
    const measure = () => setMarkdownOverflow(element.scrollHeight > markdownHeight + 1)
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [text, markdown, collapseText, expanded])
  const collapsible = collapseText && !expanded && (markdown ? markdownOverflow : shouldCollapseText(text))
  const [collapsed, setCollapsed] = useState(markdown || collapsible)
  const content = <ArkmeMessageRichText
    text={text}
    textStyle={{ fontSize: styles.text?.fontSize, lineHeight: expanded ? 1.7 : styles.text?.lineHeight }}
    {...(textFormat === undefined ? {} : { textFormat })}
    highlightMentions={highlightMentions}
    linkLabelMode={linkLabelMode}
    {...(mentionTargets === undefined ? {} : { mentionTargets })}
    {...(readMentionMembers === undefined ? {} : { readMentionMembers })}
    {...(shareWebsite === undefined ? {} : { shareWebsite })}
    {...(onMessageCopyLinkOpen === undefined ? {} : { onMessageCopyLinkOpen })}
    {...(onMentionClick === undefined ? {} : { onMentionClick })}
    {...(isMentionClickable === undefined ? {} : { isMentionClickable })}
  />
  if (!markdown && !collapsible) return <p style={{ ...styles.text, ...(expanded ? { width: '100%', lineHeight: 1.7 } : {}) }}>{content}</p>
  return <div style={styles.textFrame} {...(collapsible ? { 'data-arkme-text-collapsible': 'true' } : {})}>
    {markdown
      ? <div style={{ maxHeight: collapsible && collapsed ? markdownHeight : undefined, overflow: 'hidden' }}>
        <div ref={markdownBody}>{content}</div>
      </div>
      : <p style={{ ...styles.text, ...(expanded ? { width: '100%', lineHeight: 1.7 } : {}), ...(collapsible && collapsed ? styles.collapsedText : {}) }}>{content}</p>}
    {collapsible && collapsed && <span aria-hidden style={styles.textFade} />}
    {collapsible && <button type="button" style={styles.collapseToggle} aria-expanded={!collapsed} onClick={event => {
      preserveTextTogglePosition(event.currentTarget, () => { setCollapsed(value => !value) })
    }}>
      {collapsed ? tr("展开") : tr("收起")}
    </button>}
  </div>
}

function mediaColumns(count: number): 1 | 2 | 3 {
  if (count <= 1) return 1
  if (count <= 4) return 2
  return 3
}

type MediaFailure = 'retryable' | 'unsupported'

function VisualMediaFallback({ block, failure, onRetry, onOpenAsFile, style }: { block: ArkmeContentBlock; failure: MediaFailure; onRetry: () => void; onOpenAsFile: () => void; style?: CSSProperties }) {
  const video = block.kind === 'video'
  const label = video ? '视频' : '图片'
  const unsupported = failure === 'unsupported'
  return <button
    type="button"
    aria-label={unsupported ? `接收${label} ${block.fileName}` : `重新加载${label} ${block.fileName}`}
    data-arkme-media-fallback={unsupported ? `${block.kind}-unsupported` : block.kind}
    onClick={unsupported ? onOpenAsFile : onRetry}
    style={{ ...styles.mediaTile, ...style, display: 'grid', placeItems: 'center', color: 'var(--dsw-alias-label-secondary, #68707c)' }}
  >
    <span role="status" style={{ display: 'grid', justifyItems: 'center', gap: 7, fontSize: 12 }}>
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden>
        <rect x="3.5" y="4.5" width="21" height="19" rx="3" stroke="currentColor" strokeWidth="1.5" />
        {video
          ? <path d="m11.5 9.5 7 4.5-7 4.5v-9Z" fill="currentColor" />
          : <><circle cx="10" cy="10" r="2" fill="currentColor" /><path d="m6.5 20 5.5-5 3.5 3 2.5-2 3.5 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></>}
      </svg>
      <span>{unsupported ? tr("接收{v0}", { v0: label }) : `${label}加载失败，点击重试`}</span>
    </span>
    {video && <span style={styles.videoBadge} aria-hidden>▶ {durationLabel(block.durationSec)}</span>}
  </button>
}

function MediaGallery({ blocks, failures, retryVersions, onOpen, onFailure, onRetry, onOpenAsFile }: {
  blocks: ArkmeContentBlock[]
  failures: ReadonlyMap<string, MediaFailure>
  retryVersions: ReadonlyMap<string, number>
  onOpen: (block: ArkmeContentBlock) => void
  onFailure: (block: ArkmeContentBlock, failure: MediaFailure) => void
  onRetry: (block: ArkmeContentBlock) => void
  onOpenAsFile: (block: ArkmeContentBlock) => void
}) {
  const columns = mediaColumns(blocks.length)
  const tileSize = columns === 1 ? 150 : 75
  const width = tileSize * columns + mediaGap * (columns - 1)
  return <div
    style={{ ...styles.mediaGrid, width, gridTemplateColumns: `repeat(${String(columns)}, ${String(tileSize)}px)` }}
    data-arkme-media-count={blocks.length}
    data-arkme-media-columns={columns}
  >
    {blocks.map(block => {
      const failure = failures.get(block.mediaRef)
      if (failure !== undefined) return <VisualMediaFallback key={`${block.mediaRef}:${String(block.sortOrder)}`} block={block} failure={failure} onRetry={() => { onRetry(block) }} onOpenAsFile={() => { onOpenAsFile(block) }} />
      const src = mediaAttemptUrl(block, retryVersions.get(block.mediaRef) ?? 0)
      return <button
        key={`${block.mediaRef}:${String(block.sortOrder)}`}
        type="button"
        style={styles.mediaTile}
        aria-label={block.kind === 'video' ? `播放视频 ${block.fileName}` : tr("预览图片 {v0}", { v0: block.fileName })}
        onClick={() => { onOpen(block) }}
      >
        {block.kind === 'image'
          ? <img src={src} alt={block.fileName} loading="lazy" style={styles.mediaImage} onError={() => { onFailure(block, arkmeCanInlineLocalFile(block.mimeType, block.fileName) ? 'retryable' : 'unsupported') }} />
          : <>
            <video src={src} muted playsInline preload="metadata" style={styles.videoPreview} aria-hidden onError={event => { onFailure(block, event.currentTarget.error?.code === 4 || !arkmeCanInlineLocalFile(block.mimeType, block.fileName) ? 'unsupported' : 'retryable') }} />
            <span style={styles.videoBadge} aria-hidden>▶ {durationLabel(block.durationSec)}</span>
          </>}
        {block.kind === 'image' && block.dynamicPhoto !== undefined && <span style={{ position: 'absolute', display: 'flex', left: 6, bottom: 6, pointerEvents: 'none' }} aria-label={tr("实况照片")}>
          <ArkmeLivePhotoBadge variant="thumbnail" />
        </span>}
        <UploadProgress block={block} />
      </button>
    })}
  </div>
}

export function ArkmeFileCard({ block, fallback = false, onOpen, previewOpen = false }: { block: ArkmeContentBlock; fallback?: boolean; onOpen?: (block: ArkmeContentBlock) => void; previewOpen?: boolean }) {
  useArkmeLocale()
  const [open, setOpen] = useState(false)
  const [opening, setOpening] = useState(false)
  const controller = useRef<AbortController>()
  const original = useArkmeOriginal(block, false, open || previewOpen)
  const downloading = original.reception.state === 'receiving'
  const percent = original.reception.totalBytes > 0 ? Math.min(99, Math.floor(original.reception.receivedBytes / original.reception.totalBytes * 100)) : undefined
  const downloadLabel = original.localRef !== undefined ? '' : downloading ? `下载中${percent === undefined ? '' : ` ${percent}%`}` : original.reception.state === 'failed' ? '下载失败' : '未下载'
  const identity = block.fileAssetUid ?? block.localFileRef ?? block.originalRef ?? block.mediaRef
  useEffect(() => {
    setOpening(false)
    return () => controller.current?.abort()
  }, [identity])
  const showReception = () => { if (onOpen !== undefined) onOpen(block); else setOpen(true) }
  const activate = async () => {
    if (controller.current !== undefined && !controller.current.signal.aborted) return
    if (original.localRef === undefined || arkmeCanPreviewFile(block)) { showReception(); return }
    const request = new AbortController(); controller.current = request
    setOpening(true)
    try { await fileSdk.openLocalFile(original.localRef, request.signal) }
    catch {
      if (!request.signal.aborted) showReception()
    } finally {
      if (controller.current === request) { controller.current = undefined; setOpening(false) }
    }
  }
  return <><button type="button" aria-busy={opening} disabled={opening} onClick={event => { event.stopPropagation(); void activate() }} style={{ ...styles.file, border: 0, textAlign: 'left', cursor: opening ? 'progress' : 'pointer', position: 'relative' }} data-arkme-file-card={fallback ? 'fallback' : 'file'}>
    <span style={{ ...styles.fileIconBox, position: 'relative' }}><ArkmeFileIcon fileName={block.fileName} mimeType={block.mimeType} /><UploadProgress block={block} /></span>
    <span style={{ minWidth: 0, flex: '1 1 auto' }}><span style={styles.fileName} title={block.fileName || '未知文件'}>{block.fileName || '未知文件'}</span><small style={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: arkmeTheme.tertiary }}>{arkmeFileSize(block.size)}{downloadLabel && ` · ${downloadLabel}`}</small></span>
    {downloading && <progress aria-label={`下载 ${block.fileName}`} max={100} value={percent} style={{ position: 'absolute', left: 0, bottom: 0, height: 3, width: '100%' }} />}
  </button>{open && <ArkmeFileViewer block={block} openLocalFile onClose={() => setOpen(false)} />}</>
}

function LongArticleWordCountIcon() {
  return <svg style={styles.articleWordIcon} viewBox="0 0 14 14" fill="none" aria-hidden focusable="false">
    <path d="M8.75 1.75H4.083c-.738 0-1.166 0-1.5.167a1.5 1.5 0 0 0-.666.666c-.167.334-.167.762-.167 1.5v5.834c0 .738 0 1.166.167 1.5.146.292.374.52.666.666.334.167.762.167 1.5.167h5.834c.738 0 1.166 0 1.5-.167.292-.146.52-.374.666-.666.167-.334.167-.762.167-1.5V5.25l-3.5-3.5Z" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M8.75 1.75v2.333c0 .37 0 .584.083.75.073.146.188.261.334.334.166.083.38.083.75.083h2.333M4.667 7.583h4.666M4.667 9.625h2.916" stroke="currentColor" strokeWidth=".875" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
}

export function ArkmeForwardArticleContent({ item }: { item: ArkmeTimelineItem }) {
  useArkmeLocale()
  const [open, setOpen] = useState(false)
  return <>
    <div style={{ maxWidth: '100%', minWidth: 0, padding: '10px 13px', overflow: 'hidden', overflowWrap: 'anywhere', wordBreak: 'break-word', boxSizing: 'border-box', borderRadius: '16px 5px 16px 16px', background: arkmeTheme.messageOwn, border: '1px solid rgba(83,97,145,.045)' }}>
      <ArticleCard title={item.title} text={item.textFormat === 'markdown' ? arkmeMarkdownPlainText(item.textContent) : item.textContent} onOpen={() => { setOpen(true) }} />
    </div>
    {open && typeof document !== 'undefined' && createPortal(<ArkmeLongArticleSnapshotDialog item={item} onClose={() => { setOpen(false) }} />, document.body)}
  </>
}

function ArticleCard({ title, text, onOpen }: { title: string; text: string; onOpen: () => void }) {
  const heading = title.trim() || text.trim() || '无标题长文'
  const hasTitle = title.trim() !== ''
  const count = normalizedTextLength(text)
  return <button type="button" style={{ ...styles.article, ...styles.articleButton }} data-arkme-long-article="preview" data-arkme-long-article-inner="true" aria-label={`查看长文 ${arkmeEmojiPlainText(heading)}`} onClick={onOpen}>
    <div style={styles.articleHeading}>
      <h3 style={styles.articleTitle}><ArkmeRichText text={heading} presentation="preview" /></h3>
    </div>
    {hasTitle && text.trim() !== '' && <p style={{ ...styles.articlePreview, WebkitLineClamp: 2 }}><ArkmeRichText text={text} presentation="preview" /></p>}
    <span style={styles.articleMeta}><LongArticleWordCountIcon />{String(count)}{tr("字")}</span>
  </button>
}

type ImagePreviewMode = 'contained' | 'covered' | 'original'

export function arkmeNextImagePreviewMode(mode: ImagePreviewMode): ImagePreviewMode {
  return mode === 'contained' ? 'covered' : mode === 'covered' ? 'original' : 'contained'
}

export function arkmeImagePreviewScale(mode: ImagePreviewMode, viewportWidth: number, viewportHeight: number, imageWidth: number, imageHeight: number): number {
  if (imageWidth <= 0 || imageHeight <= 0) return 1
  if (mode === 'original') return 1
  const ratios = [viewportWidth / imageWidth, viewportHeight / imageHeight]
  return mode === 'contained' ? Math.min(...ratios) : Math.max(...ratios)
}

export function arkmeImagePreviewSpringFrames() {
  // PhotoView: AnimationController.fling(velocity: .4), Flutter's default critical spring.
  const omega = Math.sqrt(500)
  const samples = [{ time: 0, progress: 0 }]
  for (let frame = 1; frame <= 120; frame++) {
    const time = frame / 120
    const progress = Math.min(1, 1.01 + (-1.01 + (.4 - 1.01 * omega) * time) * Math.exp(-omega * time))
    samples.push({ time, progress })
    if (progress >= 1) break
  }
  const duration = samples[samples.length - 1]!.time
  return { duration: duration * 1000, frames: samples.map(({ time, progress }) => ({ offset: time / duration, progress })) }
}

function imagePreviewContentBounds(image: HTMLImageElement, mode: ImagePreviewMode): ImagePreviewRect {
  const bounds = image.getBoundingClientRect()
  const content = mode === 'contained'
    ? arkmeContainedImageRect(bounds.width, bounds.height, image.naturalWidth, image.naturalHeight)
    : { left: 0, top: 0, width: bounds.width, height: bounds.height }
  return { ...content, left: bounds.left + content.left, top: bounds.top + content.top }
}

interface ImagePreviewDragOrigin {
  clientY: number
  scrollTop: number
}

export function arkmeImagePreviewDragTop(origin: ImagePreviewDragOrigin, clientY: number): number {
  return origin.scrollTop + origin.clientY - clientY
}

export function arkmeImagePreviewAnchoredTop(imageYRatio: number, scrollHeight: number, pointerY: number): number {
  const ratio = Math.min(1, Math.max(0, imageYRatio))
  return Math.max(0, ratio * scrollHeight - pointerY)
}

interface ImagePreviewRect {
  left: number
  top: number
  width: number
  height: number
}

export function arkmeContainedImageRect(viewportWidth: number, viewportHeight: number, imageWidth: number, imageHeight: number): ImagePreviewRect {
  if (viewportWidth <= 0 || viewportHeight <= 0 || imageWidth <= 0 || imageHeight <= 0) {
    return { left: 0, top: 0, width: Math.max(0, viewportWidth), height: Math.max(0, viewportHeight) }
  }
  const scale = Math.min(viewportWidth / imageWidth, viewportHeight / imageHeight)
  const width = imageWidth * scale
  const height = imageHeight * scale
  return {
    left: (viewportWidth - width) / 2,
    top: (viewportHeight - height) / 2,
    width,
    height,
  }
}

export function ArkmeMediaPreview({ blocks, selected, onSelect, onClose, previewUrl, openLocalFile = true, forceDownload = false, navigation }: {
  navigation?: import('./ArkmeFileViewer.js').ArkmePreviewNavigation | undefined
  blocks: ArkmeContentBlock[]
  selected: ArkmeContentBlock
  onSelect: (block: ArkmeContentBlock) => void
  onClose: () => void
  previewUrl?: string
  openLocalFile?: boolean
  forceDownload?: boolean
}) {
  useArkmeLocale()
  const index = Math.max(0, blocks.findIndex(block => block.mediaRef === selected.mediaRef))
  const dialogRef = useRef<HTMLDivElement>(null)
  const filePreview = selected.kind === 'file' || forceDownload
  const viewportRef = useRef<HTMLDivElement>(null)
  const previewImageRef = useRef<HTMLImageElement>(null)
  const liveControlRef = useRef<HTMLDivElement>(null)
  const zoomAnimationRef = useRef<Animation>()
  const zoomFromRef = useRef<ImagePreviewRect>()
  const draggedRef = useRef(false)
  const blankClickTimer = useRef<ReturnType<typeof setTimeout>>()
  const cancelBlankClick = () => {
    if (blankClickTimer.current !== undefined) clearTimeout(blankClickTimer.current)
    blankClickTimer.current = undefined
  }
  const dragOriginRef = useRef<(ImagePreviewDragOrigin & { pointerId: number; clientX: number; scrollLeft: number }) | undefined>(undefined)
  const [imageMode, setImageMode] = useState<ImagePreviewMode>('contained')
  const [zoomSize, setZoomSize] = useState<{ width: number; height: number }>()
  const livePhoto = useArkmeLivePhotoPlayback(selected)
  const hasLiveControl = livePhoto.control !== null
  const original = useArkmeOriginal(selected, selected.kind === 'image')
  const keepLiveCoverPreview = selected.kind === 'image' && selected.dynamicPhoto !== undefined
    && !arkmeCanInlineLocalFile(selected.mimeType, selected.fileName) && selected.mediaRef !== selected.localFileRef
  const originalUrl = previewUrl ?? (keepLiveCoverPreview ? `${mediaRoute}?ref=${encodeURIComponent(selected.mediaRef)}`
    : original.localRef === undefined ? arkmeContentMediaUrl(selected) : arkmeLocalFileUrl(original.localRef))
  const { notice: actionNotice, showNotice: showActionNotice, clearNotice: clearActionNotice } = useArkmeFileActionNotice()
  const previousDisabled = navigation === undefined ? index <= 0 : navigation.previous === undefined
  const nextDisabled = navigation === undefined ? index >= blocks.length - 1 : navigation.next === undefined

  useLayoutEffect(() => {
    const image = previewImageRef.current
    const control = liveControlRef.current
    if (image === null || control === null) return
    const placeControl = () => {
      const canvas = image.parentElement?.getBoundingClientRect()
      if (canvas === undefined || image.naturalWidth === 0) return
      const bounds = imagePreviewContentBounds(image, imageMode)
      control.style.left = `${bounds.left - canvas.left + 14}px`
      control.style.top = `${bounds.top - canvas.top + bounds.height - 14}px`
      control.style.visibility = 'visible'
    }
    placeControl()
    image.addEventListener('load', placeControl)
    const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(placeControl)
    observer?.observe(image)
    return () => { image.removeEventListener('load', placeControl); observer?.disconnect() }
  }, [selected.mediaRef, originalUrl, imageMode, hasLiveControl])

  useEffect(() => cancelBlankClick, [selected.mediaRef, onClose])
  useEffect(() => () => {
    zoomAnimationRef.current?.cancel()
    zoomAnimationRef.current = undefined
    zoomFromRef.current = undefined
  }, [selected.mediaRef])

  useEffect(() => {
    setImageMode('contained')
    clearActionNotice()
    dragOriginRef.current = undefined
    const viewport = viewportRef.current
    if (viewport !== null) {
      viewport.scrollLeft = 0
      viewport.scrollTop = 0
    }
  }, [selected.mediaRef, clearActionNotice])

  useLayoutEffect(() => {
    const viewport = viewportRef.current
    if (viewport === null) return
    if (imageMode === 'contained') {
      viewport.scrollLeft = 0
      viewport.scrollTop = 0
    } else {
      viewport.scrollLeft = Math.max(0, (viewport.scrollWidth - viewport.clientWidth) / 2)
      viewport.scrollTop = Math.max(0, (viewport.scrollHeight - viewport.clientHeight) / 2)
    }
    const from = zoomFromRef.current
    zoomFromRef.current = undefined
    const image = previewImageRef.current
    if (from === undefined || image === null || typeof image.animate !== 'function') return
    const to = imagePreviewContentBounds(image, imageMode)
    if (to.width <= 0 || to.height <= 0) return
    const dx = from.left + from.width / 2 - to.left - to.width / 2
    const dy = from.top + from.height / 2 - to.top - to.height / 2
    const scale = from.width / to.width
    const spring = arkmeImagePreviewSpringFrames()
    zoomAnimationRef.current = image.animate(spring.frames.map(({ offset, progress }) => ({
      offset,
      transform: `translate(${dx * (1 - progress)}px, ${dy * (1 - progress)}px) scale(${1 + (scale - 1) * (1 - progress)})`,
      transformOrigin: 'center center',
    })), { duration: spring.duration, easing: 'linear' })
  }, [imageMode])

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose() }
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', closeOnEscape)
    return () => {
      window.removeEventListener('keydown', closeOnEscape)
      document.body.style.overflow = previousOverflow
    }
  }, [onClose])

  useEffect(() => {
    if (filePreview) return
    const dialog = dialogRef.current
    const previous = document.activeElement as HTMLElement | null
    dialog?.focus()
    return () => {
      if (document.activeElement === document.body || dialog?.contains(document.activeElement)) previous?.focus()
    }
  }, [filePreview])

  const toggleImageScale = () => {
    cancelBlankClick()
    const viewport = viewportRef.current
    if (viewport === null) return
    const image = previewImageRef.current
    if (image === null || image.naturalWidth <= 0 || image.naturalHeight <= 0) return
    const scaleFor = (mode: ImagePreviewMode) => arkmeImagePreviewScale(mode, viewport.clientWidth, viewport.clientHeight, image.naturalWidth, image.naturalHeight)
    const currentScale = scaleFor(imageMode)
    let nextMode = arkmeNextImagePreviewMode(imageMode)
    while (nextMode !== imageMode && scaleFor(nextMode) === currentScale) nextMode = arkmeNextImagePreviewMode(nextMode)
    if (nextMode === imageMode) return
    if (typeof image.animate === 'function' && !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      zoomFromRef.current = imagePreviewContentBounds(image, imageMode)
    }
    zoomAnimationRef.current?.cancel()
    zoomAnimationRef.current = undefined
    dragOriginRef.current = undefined
    const scale = scaleFor(nextMode)
    setZoomSize({ width: image.naturalWidth * scale, height: image.naturalHeight * scale })
    setImageMode(nextMode)
  }

  const beginImageDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    draggedRef.current = false
    if (imageMode === 'contained' || event.button !== 0 || !event.isPrimary) return
    const viewport = viewportRef.current
    if (viewport === null || (viewport.scrollHeight <= viewport.clientHeight && viewport.scrollWidth <= viewport.clientWidth)) return
    dragOriginRef.current = {
      pointerId: event.pointerId,
      clientY: event.clientY,
      clientX: event.clientX,
      scrollLeft: viewport.scrollLeft,
      scrollTop: viewport.scrollTop,
    }
    viewport.setPointerCapture(event.pointerId)
  }

  const moveImageDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    const origin = dragOriginRef.current
    const viewport = viewportRef.current
    if (origin === undefined || origin.pointerId !== event.pointerId || viewport === null) return
    if (Math.abs(event.clientY - origin.clientY) > 3 || Math.abs(event.clientX - origin.clientX) > 3) draggedRef.current = true
    viewport.scrollLeft = origin.scrollLeft + origin.clientX - event.clientX
    viewport.scrollTop = arkmeImagePreviewDragTop(origin, event.clientY)
    event.preventDefault()
  }

  const endImageDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    const origin = dragOriginRef.current
    if (origin === undefined || origin.pointerId !== event.pointerId) return
    dragOriginRef.current = undefined
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId)
  }

  const selectMedia = (block: ArkmeContentBlock) => {
    setImageMode('contained')
    dragOriginRef.current = undefined
    onSelect(block)
  }

  const closeOnBlankClick = (event: ReactMouseEvent<HTMLDivElement>) => {
    event.stopPropagation()
    cancelBlankClick()
    if (event.detail > 1) return
    if (draggedRef.current) {
      draggedRef.current = false
      return
    }
    if ((event.target as Element).closest('button, video')) return
    const image = previewImageRef.current
    if (image !== null) {
      const bounds = image.getBoundingClientRect()
      // object-fit leaves blank space inside the img element, just like the desktop photo stage.
      const content = imageMode === 'contained'
        ? arkmeContainedImageRect(bounds.width, bounds.height, image.naturalWidth, image.naturalHeight)
        : { left: 0, top: 0, width: bounds.width, height: bounds.height }
      const x = event.clientX - bounds.left
      const y = event.clientY - bounds.top
      if (x >= content.left && x < content.left + content.width && y >= content.top && y < content.top + content.height) return
    }
    // A double-click emits clicks first; wait before dismissing newly exposed blank space.
    blankClickTimer.current = setTimeout(() => {
      blankClickTimer.current = undefined
      onClose()
    }, 500)
  }

  if (filePreview) return <ArkmeFileViewer block={selected} blocks={blocks} onSelect={onSelect} onClose={onClose} openLocalFile={openLocalFile} forceDownload={forceDownload} navigation={navigation} />

  return <div ref={dialogRef} tabIndex={-1} style={styles.previewOverlay} role="dialog" aria-modal="true" aria-label={selected.fileName} onClick={onClose}
    onKeyDown={event => {
      event.stopPropagation()
      if (event.key === 'Escape' && !event.nativeEvent.isComposing) { event.preventDefault(); onClose() }
      if (event.key === 'Tab') {
        const controls = dialogRef.current?.querySelectorAll<HTMLElement>('button:not(:disabled),a[href],video[controls],audio[controls]')
        const first = controls?.[0], last = controls?.[controls.length - 1]
        if (event.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) { event.preventDefault(); last?.focus() }
        else if (!event.shiftKey && (document.activeElement === last || document.activeElement === dialogRef.current)) { event.preventDefault(); first?.focus() }
      }
    }}>
    <div style={styles.previewBody} onClick={closeOnBlankClick}>
      <style>{`
        [data-arkme-preview-close]:hover { background: rgba(20,22,24,.4) !important; }
        [data-arkme-preview-close]:active { background: rgba(20,22,24,.5) !important; }
        [data-arkme-media-preview-actions] button:not(:disabled):hover { box-shadow: inset 0 0 0 30px rgba(255,255,255,.14); }
        [data-arkme-media-preview-actions] button:not(:disabled):active { box-shadow: inset 0 0 0 30px rgba(255,255,255,.20); }
      `}</style>
      <button type="button" style={styles.previewClose} data-arkme-preview-close aria-label={tr("关闭预览")} title={tr("关闭预览")} onClick={onClose}>
        <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
          <path d="M1.0804 2.81662C0.59579 2.33201 0.595791 1.5463 1.0804 1.06169C1.56501 0.577077 2.35072 0.577077 2.83533 1.06169L8.14213 6.36849L13.4489 1.06169C13.9335 0.577075 14.7193 0.577076 15.2039 1.06169C15.6885 1.5463 15.6885 2.33201 15.2039 2.81662L9.89707 8.12342L15.2225 13.4489C15.7071 13.9335 15.7071 14.7192 15.2225 15.2038C14.7379 15.6884 13.9522 15.6884 13.4676 15.2038L8.14213 9.87835L2.81666 15.2038C2.33205 15.6884 1.54634 15.6884 1.06173 15.2038C0.577121 14.7192 0.577121 13.9335 1.06173 13.4489L6.3872 8.12342L1.0804 2.81662Z" />
        </svg>
      </button>
      <div style={styles.previewStage}>
        {selected.kind === 'image'
          ? <div
            ref={viewportRef}
            style={{
              ...styles.previewViewport,
              overflow: 'hidden',
              visibility: livePhoto.playing ? 'hidden' : 'visible',
              cursor: 'default',
            }}
            data-arkme-image-preview-viewport="true"
            data-arkme-image-preview-mode={imageMode}
            onDoubleClick={toggleImageScale}
            onWheel={event => {
              event.stopPropagation()
              if (imageMode !== 'contained') {
                event.currentTarget.scrollTop += event.deltaY
                event.currentTarget.scrollLeft += event.deltaX
              }
            }}
            onPointerDown={beginImageDrag}
            onPointerMove={moveImageDrag}
            onPointerUp={endImageDrag}
            onPointerCancel={endImageDrag}
          >
            <div style={{ ...(imageMode === 'contained' ? styles.previewCanvasContained : { width: zoomSize?.width, height: zoomSize?.height, minWidth: '100%', minHeight: '100%', display: 'grid', placeItems: 'center' }), position: 'relative' }}>
              <img
                ref={previewImageRef}
                src={originalUrl}
                alt={selected.fileName}
                draggable={false}
                style={{ ...(imageMode === 'contained' ? styles.previewImageContained : { display: 'block', width: zoomSize?.width, height: zoomSize?.height, maxWidth: 'none', userSelect: 'none' as const }), cursor: 'inherit' }}
              />
              {hasLiveControl && <div ref={liveControlRef} data-arkme-live-photo-overlay style={{ position: 'absolute', display: 'flex', visibility: 'hidden', transform: 'translateY(-100%)', zIndex: 1 }} onPointerDown={event => event.stopPropagation()} onDoubleClick={event => event.stopPropagation()}>
                {livePhoto.control}
              </div>}
            </div>
          </div>
          : <video src={originalUrl} controls autoPlay playsInline style={styles.previewMedia} aria-label={selected.fileName} />}
      </div>
      {livePhoto.video !== null && <div style={styles.previewStage}>{livePhoto.video}</div>}
      <div style={styles.previewActions} data-arkme-media-preview-actions="bottom">
        <ArkmeFileActionNavButton label={tr("上一个媒体")} direction="left" disabled={previousDisabled} onClick={() => { if (!previousDisabled) { if (navigation) navigation.previous?.(); else selectMedia(blocks[index - 1]!) } }} />
        <span aria-hidden style={styles.previewActionWideGap} />
        <ArkmeFileActionNavButton label={tr("下一个媒体")} direction="right" disabled={nextDisabled} onClick={() => { if (!nextDisabled) { if (navigation) navigation.next?.(); else selectMedia(blocks[index + 1]!) } }} />
        <span aria-hidden style={styles.previewActionWideGap} />
        <ArkmeFileActions block={selected} original={original} copySourceUrl={previewUrl ?? (selected.mediaRef === selected.localFileRef ? undefined : `${mediaRoute}?ref=${encodeURIComponent(selected.mediaRef)}`)} onImageCopyNotice={showActionNotice} showDownloadStatus={false} hideDownloadAfterSave={false} style={styles.previewActionPair} />
      </div>
      <ArkmeFileActionToast notice={actionNotice} style={styles.previewActionToast} />
    </div>
  </div>
}

function splitVisualRuns(blocks: ArkmeContentBlock[]): Array<ArkmeContentBlock | ArkmeContentBlock[]> {
  const rows: Array<ArkmeContentBlock | ArkmeContentBlock[]> = []
  for (const block of blocks) {
    if (block.renderRole === 3 && block.kind === 'image') {
      rows.push(block)
      continue
    }
    if (block.kind !== 'image' && block.kind !== 'video') {
      rows.push(block)
      continue
    }
    const tail = rows.at(-1)
    if (Array.isArray(tail)) tail.push(block)
    else rows.push([block])
  }
  return rows
}

function arkmeSharedRecordingClockText(value: number): string {
  const millis = value > 0 && value < 100_000_000_000 ? value * 1000 : value
  if (millis <= 0) return ''
  const date = new Date(millis)
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
}

export function arkmeSharedRecordingTimeText(
  recording: Pick<ArkmeSharedRecordingPreview, 'timeRangeText' | 'displayAtMillis' | 'endAtMillis'>,
): string {
  const explicit = recording.timeRangeText.trim().replace(/\s+/gu, ' ')
  if (explicit !== '') {
    const matches = [...explicit.matchAll(/(?:^|\D)([0-2]?\d):([0-5]\d)(?::[0-5]\d)?/gu)]
    if (matches.length >= 2) {
      const start = `${String(matches[0]?.[1] ?? '').padStart(2, '0')}:${matches[0]?.[2] ?? '00'}`
      const end = `${String(matches[1]?.[1] ?? '').padStart(2, '0')}:${matches[1]?.[2] ?? '00'}`
      return `${start} - ${end}`
    }
    if (matches.length === 1) return `${String(matches[0]?.[1] ?? '').padStart(2, '0')}:${matches[0]?.[2] ?? '00'}`
    return explicit
  }
  const start = arkmeSharedRecordingClockText(recording.displayAtMillis)
  const end = arkmeSharedRecordingClockText(recording.endAtMillis)
  if (start !== '' && end !== '' && end !== start) return `${start} - ${end}`
  return start || end
}

function arkmeSharedRecordingParticipantsText(recording: Pick<ArkmeSharedRecordingPreview, 'participants'>): string {
  const names = recording.participants.map(participant => participant.displayName.trim()).filter(name => name !== '')
  return names.length === 0 ? '' : `参与者：${names.join(' / ')}`
}

export function arkmeRelatedRecordingItemFromSharedRecordingPreview(
  recording: ArkmeSharedRecordingPreview,
  owner?: Pick<ArkmeTimelineItem, 'itemUid' | 'isMe'>,
): ArkmeRelatedRecordingItem {
  const startAtMillis = recording.displayAtMillis
  const endAtMillis = recording.endAtMillis > 0 ? recording.endAtMillis : startAtMillis
  const participants = recording.participants.map((participant, index) => ({
    speakerId: participant.refUserId === undefined ? `shared-recording-participant:${String(index)}` : `user:${String(participant.refUserId)}`,
    ...(participant.refUserId === undefined ? {} : { refUserId: participant.refUserId }),
    displayName: participant.displayName,
    role: participant.role,
  }))
  return {
    recordingRef: `shared-recording:${recording.sourceDigest}`,
    ...(recording.detailRef === undefined ? {} : { sharedRecordingDetailRef: recording.detailRef }),
    startAtMillis,
    endAtMillis,
    timeRangeText: arkmeSharedRecordingTimeText(recording),
    title: recording.title,
    summary: recording.summary,
    summaryStatus: 2,
    ...(recording.transcript === undefined ? {} : { transcript: recording.transcript }),
    transcriptAvailable: recording.transcriptAvailable,
    speakers: participants.map(participant => ({
      speakerId: participant.speakerId,
      ...(participant.refUserId === undefined ? {} : { refUserId: participant.refUserId }),
      nickname: participant.displayName,
    })),
    participants,
    isSharedByOther: owner?.isMe !== true,
  }
}

export function arkmeRelatedRecordingItemFromSharedRecording(item: ArkmeTimelineItem): ArkmeRelatedRecordingItem | undefined {
  return item.sharedRecording === undefined
    ? undefined
    : arkmeRelatedRecordingItemFromSharedRecordingPreview(item.sharedRecording, item)
}

export function ArkmeMessageContent({ item, sourceRef, sourceIdentityKey, onLongArticleUpdated, highlightMentions = false, collapseText = true, presentation = 'bubble', shareWebsite, onMessageCopyLinkOpen, onMentionClick, isMentionClickable, mediaSelectionIsExplicit = false, onCallDetailOpen, onArticleOpen }: {
  item: ArkmeTimelineItem
  presentation?: 'bubble' | 'detail'
  sourceRef?: string
  sourceIdentityKey?: string
  onLongArticleUpdated?: (detail: ArkmeLongArticleDetail) => void
  highlightMentions?: boolean
  collapseText?: boolean
  shareWebsite?: string
  onMessageCopyLinkOpen?: (sid: string) => void
  onMentionClick?: ArkmeMentionClickHandler
  isMentionClickable?: ArkmeMentionClickPredicate
  mediaSelectionIsExplicit?: boolean
  onCallDetailOpen?: (videoUrl?: string) => void
  onArticleOpen?: () => void
}) {
  useArkmeLocale()
  const readMentionMembers = useReadMentionMembers(item.itemUid, sourceRef)
  // Access references rotate on new messages; only a different conversation ends this media scope.
  const mediaSourceKey = sourceIdentityKey ?? sourceRef
  const lastMedia = useRef<{ sourceKey: string | undefined; item: ArkmeTimelineItem }>()
  const snapshot = lastMedia.current
  const previous = snapshot !== undefined && snapshot.sourceKey === mediaSourceKey ? snapshot.item : undefined
  const version = item.recordVersion ?? item.version
  const display = mediaSelectionIsExplicit ? item : retainPartialTimelineMedia(previous, item)
  useEffect(() => {
    lastMedia.current = { sourceKey: mediaSourceKey, item: display }
  }, [display, mediaSourceKey])
  const blocks = [...(display.contentBlocks ?? [])].sort((left, right) => left.sortOrder - right.sortOrder)
  const visualBlocks = blocks.filter(block => block.kind !== 'audio')
  const [preview, setPreview] = useState<{ sourceKey?: string | undefined; itemUid: string; fileAssetUid?: string | undefined; mediaRef: string; forceDownload?: boolean }>()
  const previewBlock = preview !== undefined && preview.sourceKey === mediaSourceKey && preview.itemUid === item.itemUid && item.status === 1
    ? visualBlocks.find(block => preview.fileAssetUid !== undefined ? block.fileAssetUid === preview.fileAssetUid : block.mediaRef === preview.mediaRef)
    : undefined
  useEffect(() => {
    if (preview !== undefined && previewBlock === undefined) setPreview(undefined)
  }, [preview, previewBlock])
  const [articleOpen, setArticleOpen] = useState(false)
  const [failures, setFailures] = useState<Map<string, MediaFailure>>(() => new Map())
  const [retryVersions, setRetryVersions] = useState<Map<string, number>>(() => new Map())
  const mediaRevision = `${sourceRef ?? ''}\0${item.itemUid}\0${String(version ?? '')}\0${blocks.map(block => `${block.mediaRef}:${block.kind}`).join(',')}`
  useEffect(() => {
    setFailures(new Map())
    setRetryVersions(new Map())
  }, [mediaRevision])
  if (item.callRecord !== undefined) return <ArkmeCallRecordContent call={item.callRecord} revision={version} onOpenDetail={onCallDetailOpen} />
  if (item.forwardRecords !== undefined) {
    const itemLines = item.forwardRecords.items.flatMap(value => {
      if (value.segments?.length) return value.segments.map(segment => `${segment.speakerName}：${segment.textContent || '语音片段'}`)
      const summary = value.title.trim() || (value.textFormat === 'markdown' ? arkmeMarkdownPlainText(value.textContent) : value.textContent) || value.contentLabel || value.contentBlocks?.[0]?.fileName || '非文本内容'
      return [`${value.senderName}：${summary}`]
    })
    const previewLines = (itemLines.length > 0 ? itemLines : item.forwardRecords.summaryLines).slice(0, 3)
    return <div style={styles.forwardCard} data-arkme-forward-records-card="true">
      <p style={styles.forwardTitle} title={arkmeEmojiPlainText(item.forwardRecords.title)}><ArkmeRichText text={item.forwardRecords.title} presentation="preview" /></p>
      <div style={styles.forwardLines}>
        {(previewLines.length > 0 ? previewLines : ['原快记暂不可查看']).map((line, index) => <p
          key={`${String(index)}:${line}`}
          style={styles.forwardLine}
        ><ArkmeRichText text={line} presentation="preview" highlightMentions={highlightMentions} /></p>)}
      </div>
    </div>
  }
  if (item.sharedRecording !== undefined) {
    const timeText = arkmeSharedRecordingTimeText(item.sharedRecording)
    const participantsText = arkmeSharedRecordingParticipantsText(item.sharedRecording)
    return <div style={styles.sharedRecordingCard} data-arkme-shared-recording-card="true">
      <div style={styles.sharedRecordingTop}>
        <p style={styles.sharedRecordingTitle} title={arkmeEmojiPlainText(item.sharedRecording.title)}><ArkmeRichText text={item.sharedRecording.title} presentation="preview" /></p>
        {timeText !== '' && <span style={styles.sharedRecordingTime}>{timeText}</span>}
      </div>
      <p style={styles.sharedRecordingSummary}><ArkmeRichText text={item.sharedRecording.summary} presentation="preview" highlightMentions={highlightMentions} /></p>
      {participantsText !== '' && <p style={styles.sharedRecordingParticipants}>{participantsText}</p>}
    </div>
  }
  const markFailed = (block: ArkmeContentBlock, failure: MediaFailure = 'retryable') => {
    setFailures(current => new Map(current).set(block.mediaRef, failure))
  }
  const retryMedia = (block: ArkmeContentBlock) => {
    setRetryVersions(current => {
      const next = new Map(current)
      next.set(block.mediaRef, (next.get(block.mediaRef) ?? 0) + 1)
      return next
    })
    setFailures(current => {
      const next = new Map(current)
      next.delete(block.mediaRef)
      return next
    })
  }
  const openPreview = (block: ArkmeContentBlock) => { setPreview({ sourceKey: mediaSourceKey, itemUid: item.itemUid, fileAssetUid: block.fileAssetUid, mediaRef: block.mediaRef }) }
  const openAsFile = (block: ArkmeContentBlock) => { setPreview({ sourceKey: mediaSourceKey, itemUid: item.itemUid, fileAssetUid: block.fileAssetUid, mediaRef: block.mediaRef, forceDownload: true }) }
  const isArticle = item.templateKind === 8 || item.displayKind === 1
  const bodyTextFormat = item.senderKind === 'bot' && item.textContent.trim() !== ''
    ? 'markdown' : item.textFormat ?? 'plain'
  const text = item.textContent || (!isArticle && blocks.length === 0 ? item.title : '')
  const linkLabelMode: ArkmeLinkLabelMode = presentation === 'detail' ? 'raw' : 'resolved'
  // A voice note's single audio owns its transcript even with photos/files.
  // Generic attachments and ambiguous multi-audio notes retain their order.
  const isVoiceNote = item.templateKind === 3 || item.templateKind === 4
  const audioBlocks = blocks.filter(block => block.kind === 'audio')
  const inlineVoice = !isArticle && audioBlocks.length === 1
    && (isVoiceNote || (item.templateKind === undefined && blocks.length === 1)) ? audioBlocks[0] : undefined
  const renderVoice = (block: ArkmeContentBlock, withTranscript = false) => <ArkmeVoiceContent
    key={block.mediaRef}
    sourceKey={`${sourceRef ?? ''}:${item.itemUid}:${block.fileAssetUid ?? block.mediaRef}`}
    src={arkmeVoiceMediaUrl(block.mediaRef)}
    durationSeconds={block.durationSec ?? (isVoiceNote && item.recordDurationMillis !== undefined ? item.recordDurationMillis / 1000 : undefined)}
    downloadName={block.fileName}
    collapsible={withTranscript && presentation !== 'detail' && collapseText && shouldCollapseText(text)}
  >{withTranscript && text !== '' ? <ArkmeMessageRichText
      text={text}
      textFormat={bodyTextFormat}
      highlightMentions={highlightMentions}
      linkLabelMode={linkLabelMode}
      {...(display.mentions === undefined ? {} : { mentionTargets: display.mentions })}
      readMentionMembers={readMentionMembers}
      {...(shareWebsite === undefined ? {} : { shareWebsite })}
      {...(onMessageCopyLinkOpen === undefined ? {} : { onMessageCopyLinkOpen })}
      {...(onMentionClick === undefined ? {} : { onMentionClick })}
      {...(isMentionClickable === undefined ? {} : { isMentionClickable })}
    /> : undefined}</ArkmeVoiceContent>
  const renderRows = splitVisualRuns(blocks.filter(block => block !== inlineVoice)).map((row, rowIndex) => {
    if (Array.isArray(row)) {
      return <div key={`visual-row:${String(rowIndex)}`} style={styles.stack}>
        <MediaGallery blocks={row} failures={failures} retryVersions={retryVersions} onOpen={openPreview} onFailure={markFailed} onRetry={retryMedia} onOpenAsFile={openAsFile} />
      </div>
    }
    const failure = failures.get(row.mediaRef)
    if (failure !== undefined) return <VisualMediaFallback key={row.mediaRef} block={row} failure={failure} onRetry={() => { retryMedia(row) }} onOpenAsFile={() => { openAsFile(row) }} {...(styles.sticker === undefined ? {} : { style: styles.sticker })} />
    if (row.renderRole === 3 && row.kind === 'image') return <img
      key={row.mediaRef}
      src={mediaAttemptUrl(row, retryVersions.get(row.mediaRef) ?? 0)}
      alt={row.fileName}
      title={row.fileName}
      draggable={false}
      style={styles.sticker}
      data-arkme-chat-sticker="true"
      onClick={() => { openPreview(row) }}
      onError={() => { markFailed(row, arkmeCanInlineLocalFile(row.mimeType, row.fileName) ? 'retryable' : 'unsupported') }}
    />
    if (row.kind === 'audio') return renderVoice(row)
    return <ArkmeFileCard key={row.mediaRef} block={row} onOpen={openPreview} previewOpen={previewBlock?.mediaRef === row.mediaRef} />
  })

  return <>
    <div style={{ ...styles.stack, ...(presentation === 'detail' ? { width: '100%' } : {}) }} data-arkme-message-content={isArticle ? 'article' : 'message'} data-arkme-content-presentation={presentation}>
      {inlineVoice !== undefined ? <>{renderVoice(inlineVoice, true)}{renderRows}</> : <>
        {isArticle && presentation === 'bubble' ? <ArticleCard title={item.title} text={item.textFormat === 'markdown' ? arkmeMarkdownPlainText(item.textContent) : item.textContent} onOpen={() => { if (onArticleOpen !== undefined) onArticleOpen(); else setArticleOpen(true) }} /> : <>
          {isArticle && item.title && <h3 style={{ margin: 0, fontSize: 14, lineHeight: 1.7 }}><ArkmeRichText text={item.title} presentation="preview" /></h3>}
          {isArticle && bodyTextFormat === 'markdown' ? <ArkmeLongArticleBody text={text} blocks={blocks} textStyle={{ fontSize: 16, lineHeight: '26px' }} /> : text !== '' && <LongText
            textFormat={bodyTextFormat}
            text={text}
            highlightMentions={highlightMentions}
            collapseText={collapseText}
            expanded={presentation === 'detail'}
            linkLabelMode={linkLabelMode}
            {...(display.mentions === undefined ? {} : { mentionTargets: display.mentions })}
            readMentionMembers={readMentionMembers}
            {...(shareWebsite === undefined ? {} : { shareWebsite })}
            {...(onMessageCopyLinkOpen === undefined ? {} : { onMessageCopyLinkOpen })}
            {...(onMentionClick === undefined ? {} : { onMentionClick })}
            {...(isMentionClickable === undefined ? {} : { isMentionClickable })}
          />}
        </>}
        {!(isArticle && bodyTextFormat === 'markdown') && renderRows}
      </>}
      {item.mediaUnavailable === true && (blocks.length > 0 || text !== '') && <p style={{ ...styles.text, color: arkmeTheme.tertiary, fontSize: 12 }}>{tr("部分媒体暂时无法加载，请刷新对话后重试")}</p>}
      {!isArticle && blocks.length === 0 && text === '' && <p style={styles.text}>
        {item.mediaUnavailable === true ? '媒体暂时无法加载' : '暂不支持的非文本内容'}
      </p>}
    </div>
    {preview !== undefined && previewBlock !== undefined && typeof document !== 'undefined' && createPortal(
      <ArkmeMediaPreview blocks={visualBlocks} selected={previewBlock} onSelect={openPreview} onClose={() => { setPreview(undefined) }} {...(preview.forceDownload === undefined ? {} : { forceDownload: preview.forceDownload })} />,
      document.body,
    )}
    {articleOpen && sourceRef !== undefined && typeof document !== 'undefined' && createPortal(
      <ArkmeLongArticleDialog
        sourceRef={sourceRef}
        item={item}
        onClose={() => { setArticleOpen(false) }}
        {...(onLongArticleUpdated === undefined ? {} : { onUpdated: onLongArticleUpdated })}
      />,
      document.body,
    )}
  </>
}

/** The drawer keeps its existing text semantics while voice notes retain media. */
export function ArkmeRecordDetailContent({ item, sourceRef, showOriginal = false }: {
  item: ArkmeTimelineItem
  sourceRef?: string | undefined
  showOriginal?: boolean
}) {
  if (item.callRecord !== undefined) return <ArkmeCallRecordContent call={item.callRecord} />
  const text = showOriginal && item.aiPolish?.originalText !== undefined
    ? item.aiPolish.originalText
    : item.aiPolish?.state === 'polished' && item.aiPolish.polishedText !== undefined
      ? item.aiPolish.polishedText : item.textContent
  if ((item.displayKind === 1 || item.templateKind === 8) && item.textFormat === 'markdown') return <ArkmeLongArticleBody text={text} blocks={item.contentBlocks} textStyle={{ fontSize: 16, lineHeight: '26px' }} />
  if (item.contentBlocks?.some(block => block.kind === 'audio') === true) {
    return <ArkmeMessageContent item={{ ...item, textContent: text }} {...(sourceRef === undefined ? {} : { sourceRef })} collapseText={false} presentation="detail" highlightMentions />
  }
  if (item.textFormat === 'markdown') return <ArkmeMarkdownBody text={text} textStyle={{ fontSize: 16, lineHeight: '26px' }} />
  return <p style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 16, lineHeight: '26px' }}><ArkmeRichText text={text || item.title || '非文本内容'} highlightMentions linkLabelMode="raw" /></p>
}

export function ArkmeAttachmentDraftTile({ asset, previewUrl, onRemove, onOpen, disabled = false }: {
  asset: Pick<ArkmeUploadedAsset, 'fileName' | 'fileKind'> & Partial<Pick<ArkmeUploadedAsset, 'mimeType'>>
  previewUrl?: string
  onRemove: () => void
  onOpen?: () => void
  disabled?: boolean
}) {
  useArkmeLocale()
  const [failedPreviewUrl, setFailedPreviewUrl] = useState<string>()
  const visualPreview = previewUrl !== undefined && failedPreviewUrl !== previewUrl && (asset.fileKind === 1 || asset.fileKind === 3)
  const imagePreviewFailed = asset.fileKind === 1 && previewUrl !== undefined && failedPreviewUrl === previewUrl
  return <span
    data-arkme-attachment-tile={visualPreview ? asset.fileKind === 1 ? 'image-preview' : 'video-preview' : imagePreviewFailed ? 'image-error' : 'file-icon'}
    title={asset.fileName}
    aria-label={`附件 ${asset.fileName}`}
    style={{ position: 'relative', width: 48, height: 48, flex: 'none', display: 'inline-grid', placeItems: 'center', overflow: 'hidden', borderRadius: 8, background: 'var(--dsw-alias-bg-subtle, #f0f2f5)' }}
  >
    <button type="button" aria-label={`预览 ${asset.fileName}`} onClick={onOpen} disabled={onOpen === undefined || imagePreviewFailed} style={{ border: 0, padding: 0, width: '100%', height: '100%', display: 'grid', placeItems: 'center', background: 'transparent', cursor: imagePreviewFailed ? 'default' : 'pointer' }}>{visualPreview && asset.fileKind === 1
      ? <img src={previewUrl} alt={asset.fileName} onError={() => { setFailedPreviewUrl(previewUrl) }} style={{ width: '100%', height: '100%', display: 'block', objectFit: 'cover' }} />
      : visualPreview && asset.fileKind === 3
        ? <><video src={previewUrl} muted playsInline preload="metadata" aria-label={asset.fileName} onError={() => { setFailedPreviewUrl(previewUrl) }} style={{ width: '100%', height: '100%', display: 'block', objectFit: 'cover', background: '#111' }} /><span aria-hidden style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', color: '#fff', fontSize: 16, textShadow: '0 1px 4px rgba(0,0,0,.55)' }}>▶</span></>
      : imagePreviewFailed
        ? <span role="status" aria-label={`${asset.fileName} 图片预览失败`} style={{ maxWidth: 42, color: 'var(--dsw-alias-label-tertiary, #7f8792)', fontSize: 9, lineHeight: '12px', textAlign: 'center' }}>{tr("图片预览失败")}</span>
        : <ArkmeFileIcon fileName={asset.fileName} {...(asset.mimeType === undefined ? {} : { mimeType: asset.mimeType })} size={32} />}</button>
    <button
      type="button"
      aria-label={`移除${asset.fileName}`}
      disabled={disabled}
      onClick={onRemove}
      style={{ position: 'absolute', top: 2, right: 2, width: 16, height: 16, display: 'grid', placeItems: 'center', border: 0, borderRadius: 999, padding: 0, background: arkmeTheme.menu, color: arkmeTheme.text, boxShadow: '0 1px 3px rgba(0,0,0,.14)', cursor: 'pointer', fontSize: 13, lineHeight: '16px' }}
    >×</button>
  </span>
}
